import React from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SurveysAPI } from '../../../API';
import { InFieldStats } from '../InFieldStats';
import { PartnerRspdtIds } from '../PartnerRspdtIds';
import { isPanelCountry, isPanelCountryByName } from '../surveyConstants';
import { MarketList } from './MarketList';
import { loiRowsFor } from './OverviewPane';
import {
  InFieldRow, PanelCounts, marketLabels, progressFromInFieldStats, statsBySurvey, statusLabel, sumPanelCounts, weightedLoi,
} from './surveyMetrics';
import { downloadPartnerUsers, downloadSurveyUsers } from './fieldworkExports';

type Props = {
  survey: any;
  // The parent's children, or [survey] on a child page.
  markets: any[];
  // All-time In-Field rows of the page's survey, shared with Overview and Markets.
  inFieldRows: InFieldRow[];
  inFieldLoaded: boolean;
  isSales: boolean;
};

type Fetched<T> = { loading: boolean; data?: T; error?: string };

type State = {
  // '' = All markets; otherwise a child survey id. A child page ignores it.
  selectedId: string;
  // getpantnerdetailsmain per scope id: the parent (all markets) and each child.
  partners: Record<string, Fetched<any[]>>;
  // getOneDXPanelOnly per panel child id.
  panel: Record<string, Fetched<PanelCounts>>;
  // Survey list rows (getAll) for Median LOI, as on Overview.
  listRows: any[] | null;
  rspdt: { partnerId: string; status: string; surveyId: string } | null;
};

const isPanelMarket = (m: any) => !!m && (isPanelCountryByName(m.country) || isPanelCountry(m.shortName));
const num = (v: any) => Number(v) || 0;
const pct = (part: number, whole: number) => (whole > 0 ? ((part / whole) * 100).toFixed(2) : '0.00');
const irPct = (completes: number, terminates: number) => (completes + terminates > 0 ? `${pct(completes, completes + terminates)}%` : '-');
const errorText = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

const NoFieldwork = () => <p className="text-muted text-center mb-0 py-2">No fieldwork yet</p>;
const Loading = () => <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>;

const ExportIcon: React.FC<{ title: string; onClick: () => void }> = ({ title, onClick }) => (
  <button type="button" className="btn btn-sm btn-success" title={title} aria-label={title} onClick={onClick}>
    <FontAwesomeIcon icon={['fas', 'download']} />
  </button>
);

export class FieldworkPane extends React.Component<Props, State> {
  private _mounted = false;

  state: State = { selectedId: '', partners: {}, panel: {}, listRows: null, rspdt: null };

  componentDidMount() {
    this._mounted = true;
    this.load();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  isChild = () => !!this.props.survey.parentid;

  // Partner and panel data for every scope is loaded once, so switching market is
  // instant and the all-markets partner grid can name each partner's markets.
  load = () => {
    const { survey, markets } = this.props;
    const scopeIds = this.isChild() ? [String(survey.id)] : [String(survey.id), ...markets.map((m) => String(m.id))];
    const panelIds = (this.isChild() ? [survey] : markets).filter(isPanelMarket).map((m) => String(m.id));

    const loading = (ids: string[]): Record<string, Fetched<any>> => Object.fromEntries(ids.map((id) => [id, { loading: true }]));
    this.setState({ partners: loading(scopeIds), panel: loading(panelIds) });

    scopeIds.forEach((id) => {
      SurveysAPI.getpantnerdetailsmain(id)
        .then((rows: any) => this.setFetched('partners', id, { loading: false, data: Array.isArray(rows) ? rows : [] }))
        .catch((e: any) => this.setFetched('partners', id, { loading: false, error: errorText(e, 'Failed to load partners') }));
    });
    panelIds.forEach((id) => {
      SurveysAPI.getOneDXPanelOnly(id)
        // The endpoint answers with a one-row array (the old grid mapped over it).
        .then((rows: any) => this.setFetched('panel', id, { loading: false, data: Array.isArray(rows) ? rows[0] : undefined }))
        .catch((e: any) => this.setFetched('panel', id, { loading: false, error: errorText(e, 'Failed to load panel stats') }));
    });
    SurveysAPI.getAll(10, 1, { uniqueid: survey.uniqueid })
      .then((res: any) => { if (this._mounted) this.setState({ listRows: Array.isArray(res) ? res : res?.data || [] }); })
      .catch(() => { if (this._mounted) this.setState({ listRows: [] }); });
  };

  setFetched = (key: 'partners' | 'panel', id: string, value: Fetched<any>) => {
    if (!this._mounted) return;
    this.setState((s) => ({ ...s, [key]: { ...s[key], [id]: value } }));
  };

  // The market the tab is scoped to; null = all markets of a parent.
  scopeMarket = (): any => {
    const { survey, markets } = this.props;
    if (this.isChild()) return survey;
    return markets.find((m) => String(m.id) === this.state.selectedId) || null;
  };

  exportUsers = (id: string, exportType?: 'panelist') => {
    downloadSurveyUsers(id, exportType).catch((e) => console.log('ERROR: Error In exporting', e));
  };

  renderMarketColumn = () => {
    const { markets, inFieldRows } = this.props;
    const { selectedId } = this.state;
    const all = progressFromInFieldStats(inFieldRows, 0);
    return (
      <div className="col-md-3">
        <div className="dx-card dx-market-list">
          <button
            type="button"
            className={`dx-market-item${selectedId === '' ? ' active' : ''}`}
            onClick={() => this.setState({ selectedId: '' })}
          >
            <span className="dx-market-text">
              <strong>All markets</strong>
              <span className="d-block text-muted small">{all.completes} completes · {all.hits} hits</span>
            </span>
          </button>
          <MarketList
            markets={markets}
            selectedId={selectedId}
            onSelect={(id) => this.setState({ selectedId: String(id) })}
            stats={statsBySurvey(inFieldRows)}
          />
        </div>
      </div>
    );
  };

  renderProjectProgress = (market: any, scopeLabel: string) => {
    const { survey, inFieldRows, inFieldLoaded } = this.props;
    const rows = market && !this.isChild() ? inFieldRows.filter((r) => String(r.surveyId) === String(market.id)) : inFieldRows;
    const quota = Number((market || survey).userLimitCommitted) || 0;
    const p = progressFromInFieldStats(rows, quota);
    const loi = this.state.listRows ? weightedLoi(loiRowsFor(market || survey, this.state.listRows)) : null;
    const tile = (label: string, value: React.ReactNode, extra?: React.ReactNode) => (
      <div className="dx-kpi" data-testid={`progress-${label}`} key={label}>
        <div className="dx-kpi-label">{label}</div>
        <div className="dx-kpi-value">{value}</div>
        {extra}
      </div>
    );
    // Four headline cards only; terminate, over-quota, dropout and hit counts are
    // in the In-Field Stats table below.
    return (
      <div className="dx-card mb-3" data-testid="project-progress">
        <h6 className="mb-3">Project progress <small className="text-muted">({scopeLabel})</small></h6>
        {!inFieldLoaded ? <Loading /> : p.hits === 0 ? <NoFieldwork /> : (
          <div className="dx-progress-grid">
            {tile('Completes', p.completes, (
              <>
                <div className="progress"><div className="progress-bar" style={{ width: `${p.pctComplete}%` }} /></div>
                <small className="text-muted">{p.quota > 0 ? `${Math.round(p.pctComplete)}% of quota` : 'No quota set'}</small>
              </>
            ))}
            {tile('Quota', p.quota)}
            {tile('IR %', p.irPct.toFixed(2))}
            {tile('Median LOI', (
              <><span>{loi === null ? '-' : loi.toFixed(2)}</span> <small className="text-muted">min</small></>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Markets a partner sends traffic to, in the all-markets view: the children whose
  // own partner rows list it (JP / JP 2 labels). A partner linked only at project
  // level applies to every market.
  partnerMarkets = (detail: any, labels: Record<string, string>): string => {
    const { markets } = this.props;
    const childFetches = markets.map((m) => ({ m, f: this.state.partners[String(m.id)] }));
    const linked = childFetches
      .filter(({ f }) => f?.data?.some((d: any) => d.partnerId === detail.partnerId))
      .map(({ m }) => labels[m.id]);
    if (linked.length) return linked.join(', ');
    if (childFetches.every(({ f }) => f && !f.loading && !f.error)) return 'All markets';
    return Array.isArray(detail.appliesTo) && detail.appliesTo.length ? detail.appliesTo.join(', ') : '-';
  };

  renderPartnerProgress = (scopeId: string, market: any, labels: Record<string, string>) => {
    const f = this.state.partners[scopeId];
    const countCell = (detail: any, status: string, value: any) => (
      <button
        type="button"
        className="btn btn-link btn-sm p-0"
        title="Show respondent ids"
        onClick={() => this.setState({ rspdt: { partnerId: detail.partnerId, status, surveyId: scopeId } })}
      >
        {value ?? 0}
      </button>
    );
    let body: React.ReactNode;
    if (!f || f.loading) body = <Loading />;
    else if (f.error) body = <Alert variant="danger" className="mb-0">{f.error}</Alert>;
    else if (!f.data || f.data.length === 0) body = <NoFieldwork />;
    else {
      body = (
        <div className="table-responsive">
          <table className="table table-sm table-bordered dx-table mb-0">
            <thead className="thead-light">
              <tr>
                <th>Partner Name</th><th>Markets</th><th>Status</th><th>Quota</th><th>Completed</th><th>Terminated</th>
                <th>Over Quota</th><th>Quality Terminated</th><th>PQ Terminated</th><th>Verisoul Blocked</th>
                <th>Hits</th><th>Dropout %</th><th>IR %</th><th>Export</th>
              </tr>
            </thead>
            <tbody>
              {f.data.map((d: any) => (
                <tr key={d.partnerId}>
                  <td>{d.partner_name ?? '-'}</td>
                  <td>{market ? labels[market.id] : this.partnerMarkets(d, labels)}</td>
                  <td>{statusLabel(d.surveyType)}</td>
                  <td>{d.quota ?? '-'}</td>
                  <td>{countCell(d, 'complete', d.completed)}</td>
                  <td>{countCell(d, 'terminate', d.terminated)}</td>
                  <td>{countCell(d, 'over_quota', d.overquota)}</td>
                  <td>{countCell(d, 'quality_terminate', d.qualityTerminated)}</td>
                  <td>{d.pq_terminated_count ?? 0}</td>
                  <td>{d.verisoul_terminate_count ?? 0}</td>
                  <td>{d.hit ?? 0}</td>
                  <td>{pct(num(d.dropout), num(d.hit))}%</td>
                  <td>{d.ir != null && Number.isFinite(Number(d.ir)) ? `${Number(d.ir).toFixed(1)}%` : '-'}</td>
                  <td className="text-center">
                    <ExportIcon
                      title={`Export ${d.partner_name || 'partner'} respondents`}
                      onClick={() => {
                        downloadPartnerUsers(scopeId, d, this.props.survey.projectId)
                          .catch((e) => console.log('ERROR: Error In exporting', e));
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <div className="dx-card mb-3" data-testid="partner-progress">
        <h6 className="mb-3">Partner progress</h6>
        {body}
      </div>
    );
  };

  renderPanelProgress = (panelMarkets: any[], allMarkets: boolean, labels: Record<string, string>) => {
    const fetches = panelMarkets.map((m) => ({ m, f: this.state.panel[String(m.id)] }));
    const loading = fetches.some(({ f }) => !f || f.loading);
    const failed = fetches.filter(({ f }) => f?.error);
    const rows = fetches.filter(({ f }) => f?.data).map(({ m, f }) => ({ m, c: f!.data as PanelCounts }));
    const total = sumPanelCounts(rows.map((r) => r.c));
    const cells = (c: PanelCounts) => (
      <>
        <td>{num(c.completed_count)}</td>
        <td>{num(c.terminated_count)}</td>
        <td>{num(c.over_quota_count)}</td>
        <td>{num(c.quality_terminated_count)}</td>
        <td>{num(c.pq_terminated_count)}</td>
        <td>{num(c.verisoul_terminate_count)}</td>
        <td>{num(c.totalhit_count)}</td>
        <td>{pct(num(c.dropout_count), num(c.totalhit_count))}%</td>
        <td>{irPct(num(c.completed_count), num(c.terminated_count))}</td>
        <td>{num(c.finalLOI) ? num(c.finalLOI).toFixed(2) : '-'}</td>
      </>
    );
    return (
      <div className="dx-card mb-3" data-testid="panel-progress">
        <h6 className="mb-3">Internal panel progress <small className="text-muted">(DataXing panel)</small></h6>
        {failed.map(({ m, f }) => (
          <Alert key={m.id} variant="danger" className="py-2">{labels[m.id]}: {f!.error}</Alert>
        ))}
        {loading ? <Loading /> : num(total.totalhit_count) === 0 ? (failed.length ? null : <NoFieldwork />) : (
          <div className="table-responsive">
            <table className="table table-sm table-bordered dx-table mb-0">
              <thead className="thead-light">
                <tr>
                  <th>Market</th><th>Completed</th><th>Terminated</th><th>Over Quota</th><th>Quality Terminated</th>
                  <th>PQ Terminated</th><th>Verisoul Blocked</th><th>Hits</th><th>Dropout %</th><th>IR %</th>
                  <th>Median LOI</th><th>Export</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ m, c }) => (
                  <tr key={m.id}>
                    <td><strong>{labels[m.id]}</strong> <span className="text-muted">{m.country}</span></td>
                    {cells(c)}
                    <td className="text-center">
                      <ExportIcon title={`Export ${labels[m.id]} panelists`} onClick={() => this.exportUsers(String(m.id), 'panelist')} />
                    </td>
                  </tr>
                ))}
                {allMarkets && (
                  <tr className="font-weight-bold bg-light">
                    <td>Total</td>
                    {cells(total)}
                    <td className="text-center">
                      <ExportIcon title="Export all panelists" onClick={() => this.exportUsers(String(this.props.survey.id), 'panelist')} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { survey, markets, isSales } = this.props;
    const isChild = this.isChild();
    const market = this.scopeMarket();
    const labels = marketLabels(isChild ? [survey] : markets);
    const scopeId = String((market || survey).id);
    const singleMarket = !!market && !isChild;
    const scopeLabel = market ? `${labels[market.id]} · ${market.country || ''}`.replace(/ · $/, '') : 'All markets';
    const panelMarkets = market ? (isPanelMarket(market) ? [market] : []) : markets.filter(isPanelMarket);

    return (
      <div className="row">
        {!isChild && this.renderMarketColumn()}
        <div className={isChild ? 'col-12' : 'col-md-9'}>
          {/* "Export All Users" stays in the page header (top right, hidden for
              sales). One market picked on a parent adds that market's export. */}
          {!isSales && singleMarket && (
            <div className="d-flex justify-content-end mb-3">
              <button type="button" className="btn btn-info btn-sm" onClick={() => this.exportUsers(scopeId)}>
                <FontAwesomeIcon icon={['fas', 'download']} className="mr-1" />
                Export {labels[market.id]} Users
              </button>
            </div>
          )}
          {this.renderProjectProgress(market, scopeLabel)}
          <InFieldStats key={scopeId} surveyId={scopeId} parentName={survey.projectname || ''} className="dx-card mb-3" />
          {this.renderPartnerProgress(scopeId, market, labels)}
          {panelMarkets.length > 0 && this.renderPanelProgress(panelMarkets, !market, labels)}
        </div>
        {this.state.rspdt && (
          <PartnerRspdtIds
            partnerId={this.state.rspdt.partnerId}
            surveyId={this.state.rspdt.surveyId}
            surveyStatus={this.state.rspdt.status}
            onClose={() => this.setState({ rspdt: null })}
          />
        )}
      </div>
    );
  }
}
